import React from "react";
import { useTranslation } from "react-i18next";

import { SCAN_TO_DEFAULT_LANGUAGE } from "../shared/constants";
import { BannerPreviewData } from "./types";
import {
    BannerSliderContainer,
    BannerSlide,
    BannerContent,
    BannerText,
    PreviewTitle,
    PreviewSubtitle,
    SliderArrow,
    SliderControls,
    SliderDot,
} from "./styled";

type BannerSectionProps = {
    bannerData: BannerPreviewData | null;
    mainColor: string;
    selectedLanguage: string;
    primaryColor: string;
    secondaryColor: string;
};

export function BannerSection({
    bannerData,
    mainColor,
    selectedLanguage,
    primaryColor,
    secondaryColor,
}: BannerSectionProps): JSX.Element {
    const { t } = useTranslation();

    // ─── Banner slider state ─────────────────────────────
    const [currentSlide, setCurrentSlide] = React.useState(0);
    const [slideDirection, setSlideDirection] = React.useState(1);

    const bannerImages = bannerData?.activeImages || [];
    const bannerEnabled = bannerData?.bannerEnabled !== false;

    const goToSlide = (index: number) => {
        setSlideDirection(index > currentSlide ? 1 : -1);
        setCurrentSlide(index);
    };

    const nextSlide = () => {
        if (bannerImages.length > 0) {
            setSlideDirection(1);
            setCurrentSlide((prev) => (prev + 1) % bannerImages.length);
        }
    };

    const prevSlide = () => {
        if (bannerImages.length > 0) {
            setSlideDirection(-1);
            setCurrentSlide(
                (prev) => (prev - 1 + bannerImages.length) % bannerImages.length
            );
        }
    };

    React.useEffect(() => {
        if (currentSlide >= bannerImages.length) {
            setCurrentSlide(Math.max(0, bannerImages.length - 1));
        }
    }, [bannerImages.length, currentSlide]);

    // If banner data provides a currentSlide, sync to it
    React.useEffect(() => {
        if (bannerData?.currentSlide !== undefined) {
            setCurrentSlide(bannerData.currentSlide);
        }
    }, [bannerData?.currentSlide]);

    const bannerTitleVisible = bannerData?.titleVisible !== false;
    const bannerDescVisible = bannerData?.descriptionVisible !== false;
    // The banner's preview language is driven only by its own picker —
    // never by the `selectedLanguage` prop, which always carries the
    // active rubric's language (MaCommande when on Ma commande, Offres
    // on Offres, etc.). Using the prop would flip the banner preview
    // every time the user clicked a language tab in another rubric's
    // section title card. Read Banniere's own broadcast
    // (`bannerData.selectedLanguage`) and fall back to the project
    // default — not the prop — so the leak can't sneak back in via a
    // null/incomplete bannerData either.
    const bannerLang =
        (bannerData as BannerPreviewData & { selectedLanguage?: string })
            ?.selectedLanguage || SCAN_TO_DEFAULT_LANGUAGE;
    const bannerTitle =
        bannerData?.customTexts?.[bannerLang]?.title ||
        "Bienvenue à notre restaurant !";
    const bannerDesc =
        bannerData?.customTexts?.[bannerLang]?.description ||
        "Cuisine française authentique";
    const bannerTitleColor = bannerData?.titleColor || "#ffffff";
    const bannerDescColor = bannerData?.descriptionColor || "#ffffff";

    return (
        <BannerSliderContainer
            bannerEnabled={bannerEnabled && bannerImages.length > 0}
        >
            {bannerImages.length > 0 ? (
                <>
                    {bannerImages.map((img, index) => (
                        <BannerSlide
                            key={img.id}
                            bannerImage={img.url}
                            isActive={index === currentSlide}
                            direction={slideDirection}
                        >
                            <BannerContent>
                                <BannerText>
                                    {bannerTitleVisible && (
                                        <PreviewTitle color={bannerTitleColor}>
                                            {bannerTitle}
                                        </PreviewTitle>
                                    )}
                                    {bannerDescVisible && (
                                        <PreviewSubtitle
                                            color={bannerDescColor}
                                        >
                                            {bannerDesc}
                                        </PreviewSubtitle>
                                    )}
                                </BannerText>
                            </BannerContent>
                        </BannerSlide>
                    ))}
                    {bannerImages.length > 1 && (
                        <>
                            <SliderArrow
                                direction="left"
                                onClick={prevSlide}
                                aria-label={t("Previous slide")}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                                </svg>
                            </SliderArrow>
                            <SliderArrow
                                direction="right"
                                onClick={nextSlide}
                                aria-label={t("Next slide")}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                                </svg>
                            </SliderArrow>
                            <SliderControls>
                                {bannerImages.map((_, index) => (
                                    <SliderDot
                                        key={index}
                                        isActive={index === currentSlide}
                                        mainColor={mainColor}
                                        onClick={() => goToSlide(index)}
                                        aria-label={t("Go to slide {{n}}", {
                                            n: index + 1,
                                        })}
                                        aria-current={
                                            index === currentSlide
                                                ? "true"
                                                : undefined
                                        }
                                    />
                                ))}
                            </SliderControls>
                        </>
                    )}
                </>
            ) : (
                <div
                    style={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#e0e0e0",
                        color: "#666",
                        fontSize: "12px",
                    }}
                >
                    {t("No active banner")}
                </div>
            )}
        </BannerSliderContainer>
    );
}
